import React, { useState } from "react";
import { Phone, Calendar, MapPin, User, Mail, Sparkles, Tag, ShieldCheck, Clock, Receipt, AlertCircle, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import OrderDetailsSkeleton from "@/components/skeleton/order-details-skeleton";
import OnlyTableSekeleton from "@/components/skeleton/only-table-skeleton";
import { formatCurrency, formatDate } from "@/lib/utils";
import getOrderTipsDetails from "@/lib/orderTipsUtils";
import getOrderVatDetails from "@/lib/orderVatUtils";
import { 
  OrderItemPriceDisplay, 
  OrderItemComboBreakdown, 
  OrderItemCourseBreakdown, 
  OrderItemMixAndMatchBreakdown, 
  OrderItemCustomizationBreakdown, 
  OrderItemSpecialMenuBreakdown 
} from "@/components/TakeOder/OrderItemComboBreakdown";
import OrderedItemDetailsModal from "@/components/TakeOder/OrderedItemDetailsModal";

const formatStatusText = (text) => {
  if (!text) return "";
  return String(text)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export default function OrderDetailsDialog({
  isOpen,
  onClose,
  order = {},
  isLoading = false,
  firstLoading = false,
}) {
  const [viewItemDetails, setViewItemDetails] = useState(null);
  const getVariantLabel = (variant) => {
    if (!variant) return "";
    if (typeof variant === "string") return variant;
    if (typeof variant === "object") {
      return variant?.name || variant?.item_type || "";
    }
    return "";
  };

  if (!order || !order.id) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-[820px] h-[90vh] overflow-auto bg-[#1a1a1a] border-neutral-700 p-0 text-white">
          <DialogTitle className="p-6 pb-2 text-xl font-semibold">Order Details</DialogTitle>
          <div className="px-6 pb-6">
            {firstLoading ? (
              <OrderDetailsSkeleton />
            ) : (
              <div className="text-center py-8 text-neutral-400">
                Loading order details...
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (isLoading && order?.id) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-[820px] h-[90vh] overflow-auto bg-[#1a1a1a] border-neutral-700 p-0 text-white">
          <DialogTitle className="p-6 pb-2 text-xl font-semibold">Order Details</DialogTitle>
          <div className="px-6 pb-6">
            <OnlyTableSekeleton row={3} header={false} />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Extract all customization types (addons, extra_addons, modifiers, sauces, toppings, salads, banquet)
  const getItemCustomizations = (item) => {
    const list = [];

    const processArray = (arr, category, isExtra = false) => {
      if (!arr || !Array.isArray(arr)) return;
      arr.forEach((elem) => {
        if (!elem) return;
        let name = typeof elem === "string" ? elem : elem.name || elem.item_name || "";
        if (elem.preFix) {
          name = `${elem.preFix} ${name}`;
        }
        if (!name) return;

        const price = Number(elem.price || elem.calculated_price || 0);
        const isCanceled = elem.canceled === true || elem.is_canceled === true || elem.status === "canceled";

        list.push({
          category,
          name,
          price,
          isExtra,
          isCanceled,
        });
      });
    };

    // 0. Base & Crust (Custom Pizza)
    const customPizza = item?.custom_pizza || item?.data?.item?.custom_pizza || null;
    const resolvedBase = (customPizza?.base_name || customPizza?.base_id) ? {
      name: customPizza.base_name,
      price: Number(customPizza.base_price || 0)
    } : (item?.base || item?.pizza_base || item?.customPizzaData?.base || null);

    const resolvedCrust = (customPizza?.crust_name || customPizza?.crust_id) ? {
      name: customPizza.crust_name,
      price: Number(customPizza.crust_price || 0)
    } : (item?.crust || item?.pizza_crust || item?.customPizzaData?.crust || null);

    if (resolvedBase) {
      const baseName = typeof resolvedBase === "string" ? resolvedBase : resolvedBase.name;
      const basePrice = typeof resolvedBase === "object" ? Number(resolvedBase.price || 0) : 0;
      if (baseName) {
        list.push({
          category: "Base",
          name: baseName,
          price: basePrice,
          isExtra: basePrice > 0,
          isCanceled: false,
        });
      }
    }

    if (resolvedCrust) {
      const crustName = typeof resolvedCrust === "string" ? resolvedCrust : resolvedCrust.name;
      const crustPrice = typeof resolvedCrust === "object" ? Number(resolvedCrust.price || 0) : 0;
      if (crustName) {
        list.push({
          category: "Crust",
          name: crustName,
          price: crustPrice,
          isExtra: crustPrice > 0,
          isCanceled: false,
        });
      }
    }

    // 1. Modifiers
    processArray(item?.modifiers, "Modifier", false);

    // 2. Addons & Extra Addons
    processArray(item?.addons || item?.add_ons, "Addon", false);
    processArray(item?.extra_addons || item?.extraAddon, "Extra Addon", true);

    // 3. Sauces & Extra Sauces
    processArray(item?.sauces, "Sauce", false);
    processArray(item?.extra_sauces, "Extra Sauce", true);

    // 4. Toppings & Extra Toppings
    processArray(item?.toppings, "Topping", false);
    processArray(item?.extra_toppings, "Extra Topping", true);

    // 5. Salads & Extra Salads
    processArray(item?.salads, "Salad", false);
    processArray(item?.extra_salads, "Extra Salad", true);

    // 6. Banquet Items
    processArray(item?.banquet_selected_items, "Banquet Item", false);
    processArray(item?.banquet_extra_items, "Banquet Extra", true);

    return list;
  };

  const orderItems = order?.items || [];
  const customerInfo = order?.customer || {};
  const deliveryInfo = order?.delivery || customerInfo?.active_address || {};

  // Formatter helper
  const formatPrice = (price) => {
    const num = Number(price);
    return isNaN(num) ? "0.00" : num.toFixed(2);
  };

  // Discount & Savings Calculations
  const discountAmount = Number(order?.discount_amount || 0);
  const loyaltyDiscountAmount = Number(order?.loyalty_discount_amount || 0);
  const spendOfferDiscount = Number(order?.applied_spend_offer?.discount_amount || 0);
  const onlineOfferDiscount = Number(order?.applied_online_discount_offer?.discount_amount || 0);
  
  const totalSavings =
    Number(order?.total_savings) ||
    spendOfferDiscount + onlineOfferDiscount + discountAmount + loyaltyDiscountAmount;

  const appliedDiscounts = order?.discounts || [];
  const appliedLoyaltyDiscounts = order?.loyalty_discounts || [];
  const isPaid = order?.payment_status === "paid";
  const paidAmount = order?.payment?.pay_amount ?? (isPaid ? Number(order?.total_price || 0) : 0);
  const changeAmt =
    order?.payment?.change_amount ||
    (paidAmount > Number(order?.total_price || 0) ? paidAmount - Number(order?.total_price || 0) : 0);
  const unpaidAmt = isPaid
    ? 0
    : Math.max(0, Number(order?.total_price || 0) - Number(order?.payment?.pay_amount || 0));

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-[820px] max-h-[92vh] overflow-y-auto bg-[#1c1c1c] border-neutral-700 p-0 gap-0 text-white shadow-2xl rounded-2xl">
          <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-800 pb-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2.5">
                  <DialogTitle className="text-xl font-bold text-white tracking-tight">
                    Order Details
                  </DialogTitle>
                  <Badge className="bg-neutral-800 text-amber-400 font-mono text-sm border-neutral-700 px-2 py-0.5">
                    #{order?.id}
                  </Badge>
                  {order?.source && (
                    <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-neutral-800 text-neutral-300 border border-neutral-700">
                      {order.source}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-neutral-400">
                  <span className="font-medium text-amber-400">
                    {formatStatusText(order?.order_type || "Pickup")} Order
                  </span>
                  {order?.day_order_number && (
                    <span className="text-neutral-500">
                      • Day #{order.day_order_number}
                    </span>
                  )}
                </div>
              </div>

              {/* Status Badges */}
              <div className="flex items-center gap-2">
                {/* Payment Badge */}
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                    isPaid
                      ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
                      : "bg-amber-500/10 text-amber-400 border border-amber-500/30"
                  }`}
                >
                  {isPaid ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                  {isPaid ? "Paid" : "Unpaid"}
                </span>

                {/* Kitchen / Stage Status */}
                {(order?.kitchen_status || order?.stage) && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-neutral-800 text-neutral-300 border border-neutral-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    {formatStatusText(order?.kitchen_status || order?.stage)}
                  </span>
                )}
              </div>
            </div>

          {/* Customer Details */}
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">
              Customer Details
            </h3>
            <div className="rounded-xl border border-neutral-800 bg-[#242424] p-4 text-sm grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              {/* Name & Unique ID */}
              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-amber-400 shrink-0" />
                <div className="truncate">
                  <span className="font-semibold text-white">
                    {customerInfo?.full_name || order?.customer_full_name || "N/A"}
                  </span>
                  {(customerInfo?.unique_id || order?.unique_id) && (
                    <span className="ml-2 inline-block font-mono text-[11px] text-amber-400 bg-neutral-900 border border-neutral-700 px-1.5 py-0.2 rounded">
                      {customerInfo?.unique_id || order?.unique_id}
                    </span>
                  )}
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-blue-400 shrink-0" />
                <span className="font-mono text-neutral-200">
                  {customerInfo?.number || order?.customer_number || "N/A"}
                </span>
              </div>

              {/* Email */}
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-purple-400 shrink-0" />
                <span className="text-neutral-300 truncate">
                  {customerInfo?.email || order?.customer_email || "No email provided"}
                </span>
              </div>

              {/* Date & Time */}
              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-emerald-400 shrink-0" />
                <span className="text-neutral-200">
                  {order?.created_at
                    ? new Date(order.created_at).toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : "N/A"}
                </span>
              </div>

              {/* Operator */}
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-4 w-4 text-teal-400 shrink-0" />
                <span className="text-neutral-300 text-xs">
                  Operator: <strong className="text-white">{order?.operator || (order?.staff_id ? `Staff #${order.staff_id}` : "N/A")}</strong>
                </span>
              </div>

              {/* Address */}
              <div className="flex items-start gap-3 sm:col-span-2 pt-1 border-t border-neutral-800/80">
                <MapPin className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
                <span className="text-neutral-300 text-xs leading-relaxed">
                  {[
                    deliveryInfo?.door_no ? `Door ${deliveryInfo.door_no}` : null,
                    deliveryInfo?.address || deliveryInfo?.address_line_1,
                    deliveryInfo?.city,
                    deliveryInfo?.postal_code || deliveryInfo?.postcode,
                    deliveryInfo?.country,
                  ]
                    .filter(Boolean)
                    .join(", ") || "No address specified"}
                </span>
              </div>
            </div>
          </div>

          {/* Item Details */}
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">
              Item Details ({orderItems.length})
            </h3>
            <div className="rounded-xl border border-neutral-800 bg-[#242424] overflow-hidden">
              <div className="grid grid-cols-[3fr,1fr,1.5fr] gap-3 p-3 bg-neutral-900/60 border-b border-neutral-800 text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                <div>Item Name</div>
                <div className="text-center">Quantity</div>
                <div className="text-right">Price</div>
              </div>
              <div className="divide-y divide-neutral-800/60">
                {orderItems.length > 0 ? (
                  orderItems.map((item, index) => {
                    const qty = Number(item?.quantity || 1);
                    const calculatedRowPrice = Number(item?.calculated_price ?? item?.price ?? item?.base_price ?? 0);
                    const totalItemPrice = calculatedRowPrice * qty;

                    return (
                      <div key={item?.order_item_id || item?.id || index} className="p-3.5 text-xs">
                        <div className="grid grid-cols-[3fr,1fr,1.5fr] gap-3 items-center">
                          <div>
                            <div className="font-semibold text-white text-sm flex flex-wrap items-center gap-1.5">
                              <span>{item?.name || "N/A"}</span>
                              {getVariantLabel(item?.variant) && (
                                <span className="text-amber-400 text-xs font-normal">
                                  ({getVariantLabel(item?.variant)})
                                </span>
                              )}
                              {item?.size && (
                                <span className="text-amber-400 text-xs font-normal">
                                  ({item.size})
                                </span>
                              )}
                              {item?.buffet_price_for && (
                                <span className="text-neutral-400 text-[11px]">
                                  ({item.buffet_price_for === "adult" ? "Adult" : "Kids"})
                                </span>
                              )}
                              {item?.is_offer_discount && (
                                <Badge className="bg-emerald-600/20 text-emerald-400 border-emerald-500/40 text-[10px] py-0 px-1.5">
                                  🏷️ Offer
                                </Badge>
                              )}
                              {item?.is_complementary && (
                                <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/40 text-[10px] py-0 px-1.5">
                                  ✨ Complementary
                                </Badge>
                              )}
                              {(item?.is_loyalty_free || item?.is_offer_free || item?.is_free) && !item?.is_complementary && (
                                <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px] py-0 px-1.5">
                                  Free
                                </Badge>
                              )}
                              {item?.is_replaced && (
                                <Badge size="sm" variant="destructive" className="bg-red-500/20 text-red-400 border-red-500/30 text-[10px] py-0">
                                  Replaced
                                </Badge>
                              )}
                            </div>
                          </div>

                          <div className="text-center font-medium text-neutral-200">
                            <span className="inline-block px-2 py-0.5 rounded bg-neutral-800 border border-neutral-700">
                              {qty}
                            </span>
                          </div>

                          <div className="flex items-center justify-end gap-2 text-right flex-wrap">
                            <OrderItemPriceDisplay item={item} orderType={order?.order_type} />
                            <span className="text-amber-400 font-bold min-w-[65px]">
                              £{totalItemPrice.toFixed(2)}
                            </span>
                            <button
                              type="button"
                              onClick={() => setViewItemDetails(item)}
                              className="text-xs text-amber-400 hover:text-amber-300 underline underline-offset-2 transition-colors cursor-pointer whitespace-nowrap ml-1 font-medium"
                            >
                              View Details
                            </button>
                          </div>
                        </div>

                        {/* Breakdown Components */}
                        <OrderItemComboBreakdown comboItems={item?.combo_items} />
                        <OrderItemCourseBreakdown courseItems={item?.course_items} item={item} />
                        <OrderItemSpecialMenuBreakdown specialMenu={item?.special_menu} item={item} />
                        <OrderItemMixAndMatchBreakdown items={item?.mix_and_match_items || item?.selected_items || item?.categories} />
                        <OrderItemCustomizationBreakdown item={item} />

                        {/* Replaced item new items stack */}
                        {item.is_replaced && Array.isArray(item.new_items) && item.new_items.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-neutral-800 flex flex-col gap-1.5">
                            <span className="text-[11px] font-semibold text-emerald-400">New Replacement Items:</span>
                            {item.new_items.map((newItem, nIdx) => (
                              <div key={nIdx} className="bg-neutral-900/80 rounded p-2.5 flex flex-col gap-1.5 border border-neutral-800">
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="text-xs font-semibold text-white">{newItem.name}</span>
                                    {newItem?.variant?.name && (
                                      <span className="text-[10px] text-amber-400">({newItem.variant.name})</span>
                                    )}
                                    <span className="text-[10px] text-amber-400 font-bold">x{newItem.quantity || 1}</span>
                                    <span className="px-1.5 py-0.2 bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 text-[9px] rounded font-semibold">New</span>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <OrderItemPriceDisplay item={newItem} orderType={order?.order_type} />
                                    <span className="text-amber-400 text-xs font-bold">
                                      £ {(Number(newItem.calculated_price ?? newItem.price ?? 0) * Number(newItem.quantity || 1)).toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                                <OrderItemComboBreakdown comboItems={newItem.combo_items} />
                                <OrderItemCourseBreakdown courseItems={newItem.course_items} item={newItem} />
                                <OrderItemSpecialMenuBreakdown specialMenu={newItem.special_menu} item={newItem} />
                                <OrderItemMixAndMatchBreakdown items={newItem.mix_and_match_items || newItem.selected_items || newItem.categories} />
                                <OrderItemCustomizationBreakdown item={newItem} />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="p-6 text-center text-neutral-500 text-xs">No items found</div>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          {order?.note && (
            <div className="space-y-2">
              <h3 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">Order Note</h3>
              <div className="rounded-xl border border-neutral-800 bg-[#242424] p-3.5 text-xs text-neutral-200 leading-relaxed">
                {order.note}
              </div>
            </div>
          )}

          {/* Payment Details */}
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">Payment Details</h3>
            <div className="rounded-xl border border-neutral-800 bg-[#242424] p-4 sm:p-5 space-y-3 text-xs">
              {/* Gross Order Total */}
              <div className="flex justify-between text-neutral-300">
                <span>Order Total</span>
                <span className="font-semibold text-white">£{formatPrice(order?.order_total || order?.subtotal)}</span>
              </div>

              {/* Spend Offer Discount / Free Item */}
              {order?.applied_spend_offer && (
                <div className="flex justify-between text-emerald-400 bg-emerald-950/20 px-2.5 py-1.5 rounded-lg border border-emerald-800/30">
                  <span className="flex items-center gap-1 font-medium">
                    <Sparkles className="w-3 h-3" /> Offer ({order.applied_spend_offer.name})
                  </span>
                  <span className="font-semibold">
                    {order.applied_spend_offer.discount_amount > 0
                      ? `-£${formatPrice(order.applied_spend_offer.discount_amount)}`
                      : `Free Item Applied`}
                  </span>
                </div>
              )}

              {/* Online Discount Offer */}
              {order?.applied_online_discount_offer && (
                <div className="flex justify-between text-emerald-400 bg-emerald-950/20 px-2.5 py-1.5 rounded-lg border border-emerald-800/30">
                  <span className="flex items-center gap-1 font-medium">
                    <Sparkles className="w-3 h-3" /> Online Offer ({order.applied_online_discount_offer.name})
                  </span>
                  <span className="font-semibold">
                    -£{formatPrice(order.applied_online_discount_offer.discount_amount)}
                  </span>
                </div>
              )}

              {/* Discounts list */}
              {appliedDiscounts.map((discount, index) => (
                <div key={`discount-${discount?.id || index}`} className="flex justify-between pl-2 text-emerald-400">
                  <span>- {discount?.discount_name || discount?.name || "Discount"}</span>
                  <span>-£{formatPrice(Number(discount?.discount_amount || 0))}</span>
                </div>
              ))}

              {/* Loyalty Discounts */}
              {appliedLoyaltyDiscounts.map((loyalty, index) => (
                <div key={`loyalty-${loyalty?.loyalty_short_code || index}`} className="flex justify-between pl-2 text-emerald-400">
                  <span>- {loyalty?.loyalty_short_code || loyalty?.loyalty_discount_code || "Loyalty"}</span>
                  <span>-£{formatPrice(Number(loyalty?.loyalty_discount_amount || 0))}</span>
                </div>
              ))}

              {/* Points */}
              {order?.used_points > 0 && (
                <div className="flex justify-between text-neutral-300">
                  <span>Points Used ({order?.used_points})</span>
                  <span className="text-emerald-400">-£{formatPrice(order?.points_value)}</span>
                </div>
              )}

              {/* Service Charge */}
              {Number(order?.service_charge_amount || 0) > 0 && (
                <div className="flex justify-between text-neutral-300">
                  <span>Service Charge</span>
                  <span>£{formatPrice(order?.service_charge_amount)}</span>
                </div>
              )}

              {/* Delivery Charge */}
              {Number(order?.delivery_charge || 0) > 0 && (
                <div className="flex justify-between text-neutral-300">
                  <span>Delivery Charge</span>
                  <span>£{formatPrice(order?.delivery_charge)}</span>
                </div>
              )}

              {/* Total Savings Highlight */}
              {totalSavings > 0 && (
                <div className="flex justify-between text-emerald-400 font-semibold pt-1 border-t border-neutral-800">
                  <span className="flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5" /> Total Savings
                  </span>
                  <span>£{formatPrice(totalSavings)}</span>
                </div>
              )}

              {/* Subtotal */}
              <div className="flex justify-between text-neutral-300 pt-1 border-t border-neutral-800">
                <span>Sub Total (Net)</span>
                <span className="font-semibold text-white">£{formatPrice(order?.subtotal)}</span>
              </div>

              {/* VAT */}
              {(() => {
                const vatInfo = getOrderVatDetails(order);
                return (
                  <div className="flex justify-between text-neutral-400">
                    <span>Total VAT {vatInfo.vatTypeLabel}</span>
                    <span>£{formatPrice(order?.total_vat || 0)}</span>
                  </div>
                );
              })()}

              {/* Tips */}
              {(() => {
                const tipsInfo = getOrderTipsDetails(order);
                if (!tipsInfo.hasTips) return null;
                return (
                  <>
                    <div className="flex justify-between text-neutral-300">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span>Tips</span>
                        {tipsInfo.tipsMode === "course_wise" && (
                          <span className="bg-yellow-500/20 text-yellow-400 text-[10px] px-1.5 py-0.5 rounded font-medium">Course-wise</span>
                        )}
                        {tipsInfo.tipsMode === "split" && (
                          <span className="bg-blue-500/20 text-blue-400 text-[10px] px-1.5 py-0.5 rounded font-medium">Split</span>
                        )}
                      </div>
                      <span className="text-yellow-400 font-semibold">£{formatPrice(tipsInfo.totalTips)}</span>
                    </div>
                    {tipsInfo.breakdown && tipsInfo.breakdown.length > 0 && (
                      <div className="-mt-1 mb-1 pl-2 pr-1.5 py-1 bg-black/30 rounded border border-white/5 flex flex-col gap-0.5">
                        {tipsInfo.breakdown.map((person, idx) => (
                          <div key={idx} className="flex justify-between items-center text-[11px] text-neutral-400">
                            <span className="truncate">
                              • {person.payerName} {person.tipType === "percentage" ? `(${person.tipInputAmount}%)` : `(Fixed)`}:
                            </span>
                            <span className="font-medium text-neutral-300 shrink-0">
                              £{formatPrice(person.tipAmount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Bill Amount */}
              <div className="flex justify-between text-sm border-t border-dashed border-neutral-700 pt-2 font-bold">
                <span className="text-amber-400">Bill Amount</span>
                <span className="text-amber-400">£{formatPrice(order?.total_price)}</span>
              </div>

              {/* Total To Pay */}
              <div className="flex justify-between text-sm font-bold text-white pt-1">
                <span>TOTAL TO PAY</span>
                <span>£{formatPrice(order?.total_price)}</span>
              </div>

              {/* Paid */}
              <div className="flex justify-between text-neutral-300">
                <span>Paid</span>
                <span className="font-medium text-white">£{formatPrice(paidAmount)}</span>
              </div>

              {/* Change */}
              {changeAmt > 0 && (
                <div className="flex justify-between text-neutral-300">
                  <span>Change Amount</span>
                  <span>£{formatPrice(changeAmt)}</span>
                </div>
              )}

              {/* Unpaid */}
              {unpaidAmt > 0 && (
                <div className="flex justify-between text-xs font-bold text-red-400">
                  <span>UNPAID AMOUNT</span>
                  <span>£{formatPrice(unpaidAmt)}</span>
                </div>
              )}

              {/* Bottom Footer Ribbon */}
              <div className="pt-3 mt-2 border-t border-neutral-700 flex justify-between items-center">
                <div>
                  <span
                    className={`text-sm font-bold capitalize px-2.5 py-1 rounded-md ${
                      isPaid
                        ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                        : "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                    }`}
                  >
                    {isPaid ? "Paid" : formatStatusText(order?.stage || "Unpaid")}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-[11px] text-neutral-400 font-medium">Total Payable</div>
                  <div className="text-lg font-extrabold text-amber-400">
                    £{formatPrice(order?.total_price)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Full Deep-Dive Item Details Modal */}
    {viewItemDetails && (
      <OrderedItemDetailsModal
        isOpen={Boolean(viewItemDetails)}
        onClose={() => setViewItemDetails(null)}
        item={viewItemDetails}
        orderType={order?.order_type}
      />
    )}
  </>
);
}
